import { type RefObject, useLayoutEffect, useState } from "react";

export const DEFAULT_SELECT_COLUMN_SIZE = 48;

// The 16px checkbox plus 8px before the next column's own padding.
const CHECKBOX_AND_SLACK_PX = 24;

/**
 * Sizes the selection column to its left padding (the page gutter, or a
 * container's `--view-table-select-padding-left`) plus the checkbox and 8px,
 * so the first data column keeps one distance from the checkbox: 40px inside
 * the app shell, 48px in drawers and modals. The padding is read before first
 * paint; the width has to be a number because pinned columns take their
 * sticky offsets from it.
 */
export function useSelectColumnSize({
    containerRef,
    selectCellClassName,
    hasSelectColumn,
}: {
    containerRef: RefObject<HTMLElement | null>;
    selectCellClassName: string;
    hasSelectColumn: boolean;
}): number {
    const [size, setSize] = useState(DEFAULT_SELECT_COLUMN_SIZE);

    useLayoutEffect(() => {
        const selectCell = containerRef.current?.querySelector(
            `.${selectCellClassName}`,
        );
        if (!hasSelectColumn || !selectCell) {
            return;
        }
        const paddingLeft = parseFloat(
            getComputedStyle(selectCell).paddingLeft,
        );
        if (Number.isFinite(paddingLeft)) {
            setSize(Math.round(paddingLeft) + CHECKBOX_AND_SLACK_PX);
        }
    }, [containerRef, selectCellClassName, hasSelectColumn]);

    return size;
}
